import { Component, inject, OnInit, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { AuthenticationService } from '../core/authentication/authentication.service';
import { Router } from '@angular/router';
import { AppUrls } from '../routing/app-urls';
import { SignedInUser } from '../shared/types/user';
import { UserService } from '../core/services/user.service';
import { ThemeService, Theme } from '../core/services/theme.service';
import { ChainService } from '../core/services/chain.service';
import { ButtonComponent, ButtonType } from '../shared/components/button/button.component';
import { generateErrorId } from '../shared/utils/unique-id-utils';
import { ClientLoggingService } from '../core/services/client-logging.service';

@Component({
    selector: 'juyo-sign-in-page',
    styleUrl: 'sign-in.scss',
    imports: [ReactiveFormsModule, ButtonComponent],
    template: `
        <div class="page-body">
            <div class="form-container">
                <div class="panel">
                    @if (themeService.theme() === Theme.Dark) {
                        <img class="logo" src="../../assets/logos/logo_white.png" alt="logo" />
                    } @else {
                        <img class="logo" src="../../assets/logos/logo_dark.png" alt="logo" />
                    }

                    <div class="heading-section">
                        <span class="sign-in-label">SIGN IN</span>
                        <h1 class="heading">Welcome back</h1>
                        <p class="subtitle">Access your space to connect the dots.</p>
                    </div>

                    <div class="login-failed">
                        @if (showInvalidUsername()) {
                            Oops! The email / username you entered is not valid.
                        }
                        @if (showFailedAttempt()) {
                            Oops! The email or password you entered is incorrect. Please try again.
                        }
                        @if (showUserEndpointError()) {
                            Oops! We couldn't retrieve your user information. Please try refreshing the page or contact
                            support if the issue persists.
                        }
                        @if (showHotelEndpointError()) {
                            Oops! We couldn't load your hotel data. Please try refreshing the page or contact support if
                            the issue persists.
                        }
                    </div>

                    <form class="form" [formGroup]="form" (keydown)="onKeyDown($event)" novalidate>
                        <label class="field-label" for="username">USERNAME</label>
                        <input
                            type="text"
                            name="username"
                            id="username"
                            #usernameInput
                            class="field"
                            placeholder="james@organization.com"
                            formControlName="username"
                        />
                        @if (!showPasswordInput()) {
                            <juyo-button
                                [title]="'NEXT'"
                                [isLoading]="verifyDomainInProgress()"
                                [useFullWidth]="true"
                                [buttonType]="ButtonType.PURPLE"
                                (clicked)="onCheckIdp()"
                            >
                                Next
                            </juyo-button>
                        }
                        @if (showPasswordInput()) {
                            <label class="field-label" for="password">PASSWORD</label>
                            <input
                                type="password"
                                name="password"
                                id="password"
                                class="field"
                                placeholder="Enter your password"
                                formControlName="password"
                            />
                            <juyo-button
                                [title]="'SIGN IN'"
                                [isLoading]="signInInProgress()"
                                [useFullWidth]="true"
                                [buttonType]="ButtonType.PURPLE"
                                (clicked)="onSignIn()"
                            >
                                Sign In
                            </juyo-button>
                        }
                    </form>

<!--                    <div class="forgot-password">-->
<!--                        <a href="#">Forgot your password?</a>-->
<!--                    </div>-->
                </div>
            </div>

            <footer class="footer">
                <span>© 2026 Juyo Analytics</span>
                <span class="separator">·</span>
                <a target="_blank" href="https://juyoanalytics.com/privacy-policy-and-cookies/">Privacy</a>
                <span class="separator">·</span>
                <a target="_blank" href="https://juyoanalytics.com/privacy-policy-and-cookies/">Terms</a>
            </footer>
        </div>
    `,
})
export class SignInPage implements OnInit {
    public form: FormGroup<LoginForm>;
    public showInvalidUsername = signal(false);
    public showFailedAttempt = signal(false);
    public showUserEndpointError = signal(false);
    public showHotelEndpointError = signal(false);
    public signInInProgress = signal(false);
    public verifyDomainInProgress = signal(false);
    public showPasswordInput = signal(false);

    private authenticationService = inject(AuthenticationService);
    private clientLoggingService = inject(ClientLoggingService);
    private chainService = inject(ChainService);
    private router = inject(Router);
    private userService = inject(UserService);
    protected themeService = inject(ThemeService);

    protected readonly Theme = Theme;
    protected readonly ButtonType = ButtonType;

    constructor() {
        this.form = new FormGroup<LoginForm>({
            username: new FormControl('', [Validators.required, Validators.maxLength(50)]),
            password: new FormControl('', [Validators.required, Validators.maxLength(50)]),
        });
    }

    public ngOnInit() {
        this.verifyDomainInProgress.set(true);
        this.signInInProgress.set(true);
        // navigate to landing page if a valid session is found
        this.authenticationService.isSessionValid().then((isSessionValid) => {
            if (isSessionValid) {
                this.loadUser();
            } else {
                this.verifyDomainInProgress.set(false);
                this.signInInProgress.set(false);
                this.authenticationService.signOut();
            }
        });
    }

    public async onCheckIdp() {
        this.verifyDomainInProgress.set(true);
        const formModel = this.form.value;
        const username = formModel.username;
        if (username) {
            this.authenticationService.getUserIdpInfo(username).subscribe((userIdpInfo) => {
                if (userIdpInfo && userIdpInfo.idp && userIdpInfo.valid) {
                    this.showInvalidUsername.set(false);
                    if (userIdpInfo.idp === 'Cognito') {
                        this.verifyDomainInProgress.set(false);
                        this.showPasswordInput.set(true);
                    } else {
                        this.authenticationService.federatedSignIn(userIdpInfo.idp);
                    }
                } else {
                    this.showInvalidUsername.set(true);
                }
            });
        }
    }

    public async onSignIn() {
        try {
            this.signInInProgress.set(true);
            const formModel = this.form.value;
            const success = await this.authenticationService.signIn(
                formModel.username as string,
                formModel.password as string,
            );
            if (success) {
                this.loadUser();
            } else {
                // this will probably never happen, as the signIn will throw an error instead which will be caught below
                this.handleError('Unable to log in at Cognito', ErrorType.Login);
            }
        } catch (err) {
            this.handleError(err, ErrorType.Login);
        }
    }

    public onKeyDown(event: KeyboardEvent) {
        if (event.key !== 'Enter') {
            this.showFailedAttempt.set(false);
        }
    }

    public navigateAfterSignin() {
        this.chainService.loadChains().subscribe({
            next: (chains) => {
                if (chains && chains.length > 0) {
                    this.showHotelEndpointError.set(false);
                    this.verifyDomainInProgress.set(false);
                    this.signInInProgress.set(false);
                    this.router.navigateByUrl(AppUrls.hotelGptUrl);
                } else {
                    this.router.navigate([AppUrls.noAccessUrl]);
                }
            },
            error: (error) => {
                this.handleError(error, ErrorType.HotelEndpoint);
            },
        });
    }

    private loadUser() {
        this.userService.loadUser(true).subscribe({
            next: (user: SignedInUser | null) => {
                if (user && user.id) {
                    this.showUserEndpointError.set(false);
                    this.navigateAfterSignin();
                } else {
                    this.handleError('Something went wrong while loading the user!', ErrorType.UserEndpoint);
                }
            },
            error: (error: Error) => {
                this.handleError(error, ErrorType.UserEndpoint);
            },
        });
    }

    private handleError(error: any = null, errorType: ErrorType) {
        const correlationId = generateErrorId();
        this.clientLoggingService.log(correlationId, error);

        if (errorType === ErrorType.Login) {
            this.showFailedAttempt.set(true);
        } else if (errorType === ErrorType.UserEndpoint) {
            this.showUserEndpointError.set(true);
        } else if (errorType === ErrorType.HotelEndpoint) {
            this.showHotelEndpointError.set(true);
        }

        this.signInInProgress.set(false);
    }
}

interface LoginForm {
    username: FormControl<string | null>;
    password: FormControl<string | null>;
}

enum ErrorType {
    Login,
    UserEndpoint,
    HotelEndpoint,
}
